import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Award, Users, Heart, Target, Shield, Lightbulb } from "lucide-react";
import { motion } from "framer-motion";

const AboutUs = () => {
  const values = [
    {
      icon: Heart,
      title: "Pasión por la Calidad",
      description: "Cada proyecto es una oportunidad de crear espacios excepcionales que superen las expectativas."
    },
    {
      icon: Users,
      title: "Atención Personalizada",
      description: "Nuestro equipo de expertos brinda asesoría especializada en cada etapa del proyecto."
    },
    {
      icon: Shield,
      title: "Garantía y Confianza",
      description: "Respaldamos nuestro trabajo con garantías sólidas y un servicio post-venta excepcional."
    },
    {
      icon: Lightbulb,
      title: "Innovación Constante",
      description: "Incorporamos las últimas tendencias y tecnologías en acabados residenciales."
    }
  ];

  const achievements = [
    { number: "15+", label: "Años de Experiencia" },
    { number: "500+", label: "Proyectos Completados" },
    { number: "98%", label: "Clientes Satisfechos" },
    { number: "24h", label: "Tiempo de Respuesta" }
  ];

  return (
    <section id="nosotros" className="py-20 bg-gradient-to-b from-[#F7F4F0] via-[#FBF9F6] to-[#F7F4F0] relative">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center gap-2 bg-[#C6A76D]/10 px-4 py-2 rounded-full mb-4">
            <Award className="w-4 h-4 text-[#C6A76D]" />
            <span className="text-sm font-medium text-[#C6A76D]">Nuestra Historia</span>
          </div>
          <motion.h2 
            className="font-playfair text-4xl md:text-5xl font-bold text-[#2A2520] mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Sobre El Samán
          </motion.h2>
          <motion.p 
            className="text-xl text-[#4B2E22] max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Somos especialistas en crear ambientes únicos a través de acabados residenciales de la más alta calidad.
          </motion.p>
        </motion.div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="group relative bg-gradient-to-br from-white to-[#F7F4F0] border-2 border-transparent hover:border-[#C6A76D] shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 overflow-hidden">
              {/* Animated border glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-0 bg-gradient-to-r from-[#C6A76D]/0 via-[#C6A76D]/20 to-[#C6A76D]/0 blur-xl"></div>
              </div>
              <CardHeader className="relative z-10">
                <CardTitle className="font-playfair text-2xl text-[#2A2520] flex items-center group-hover:text-[#C6A76D] transition-colors">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C6A76D] to-[#B0975F] flex items-center justify-center mr-3 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <Target className="text-white" size={24} />
                  </div>
                  Nuestra Misión
                </CardTitle>
              </CardHeader>
              <CardContent className="relative z-10">
                <p className="text-[#4B2E22] leading-relaxed">
                  Transformar espacios residenciales a través de acabados de lujo, ofreciendo productos premium 
                  y asesoría especializada que supere las expectativas de nuestros clientes. Nos comprometemos 
                  a crear ambientes que reflejen elegancia, funcionalidad y personalidad única.
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="group relative bg-gradient-to-br from-white to-[#F7F4F0] border-2 border-transparent hover:border-[#C6A76D] shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 overflow-hidden">
              {/* Animated border glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-0 bg-gradient-to-r from-[#C6A76D]/0 via-[#C6A76D]/20 to-[#C6A76D]/0 blur-xl"></div>
              </div>
              <CardHeader className="relative z-10">
                <CardTitle className="font-playfair text-2xl text-[#2A2520] flex items-center group-hover:text-[#C6A76D] transition-colors">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C6A76D] to-[#B0975F] flex items-center justify-center mr-3 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <Award className="text-white" size={24} />
                  </div>
                  Nuestra Visión
                </CardTitle>
              </CardHeader>
              <CardContent className="relative z-10">
                <p className="text-[#4B2E22] leading-relaxed">
                  Ser la empresa líder en acabados residenciales de lujo, reconocida por la excelencia en 
                  nuestros productos, la innovación en nuestras soluciones y la satisfacción total de 
                  nuestros clientes. Aspiramos a estar presentes en cada hogar que busque distinción y calidad.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Values */}
        <motion.div 
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.h3 
            className="font-playfair text-3xl font-bold text-center text-[#2A2520] mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Nuestros Valores
          </motion.h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="text-center group relative bg-white border-2 border-transparent hover:border-[#C6A76D] shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-4 overflow-hidden h-full">
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#C6A76D]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <CardContent className="pt-8 relative z-10">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#C6A76D] to-[#B0975F] rounded-full mb-4 group-hover:scale-125 group-hover:rotate-12 group-hover:shadow-lg transition-all duration-300 shadow-md">
                      <value.icon className="text-white" size={28} />
                    </div>
                    <h4 className="font-playfair text-xl font-semibold text-[#2A2520] mb-3 group-hover:text-[#C6A76D] transition-colors">
                      {value.title}
                    </h4>
                    <p className="text-[#4B2E22] leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Achievements */}
        <motion.div 
          className="bg-gradient-to-br from-[#C6A76D] to-[#B0975F] p-8 rounded-2xl shadow-xl relative overflow-hidden"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {/* Decorative pattern overlay */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: "radial-gradient(circle at 2px 2px, #4B2E22 1px, transparent 0)",
              backgroundSize: "40px 40px"
            }}></div>
          </div>
          
          <motion.h3 
            className="font-playfair text-3xl font-bold text-center text-white mb-12 relative z-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Nuestros Logros
          </motion.h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
            {achievements.map((achievement, index) => (
              <motion.div 
                key={index} 
                className="text-center group"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.1, y: -5 }}
              >
                <div className="text-5xl md:text-6xl font-bold text-white mb-2 group-hover:text-[#F3ECE5] transition-colors">
                  {achievement.number}
                </div>
                <div className="text-white/90 font-medium">
                  {achievement.label}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Why Choose Us */}
        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.h3 
            className="font-playfair text-3xl font-bold text-[#2A2520] mb-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            ¿Por Qué Elegir El Samán?
          </motion.h3>
          <div className="max-w-4xl mx-auto space-y-6 text-[#4B2E22] leading-relaxed mb-8">
            <p>
              Con más de 15 años de experiencia en el mercado, nos hemos consolidado como la empresa de referencia 
              en acabados residenciales de lujo. Nuestro compromiso con la excelencia nos ha permitido crear 
              relaciones duraderas con clientes que confían en nosotros para transformar sus espacios más importantes.
            </p>
            <p>
              Trabajamos únicamente con productos de la más alta calidad, respaldados por garantías sólidas y 
              un servicio integral que incluye asesoría, diseño, instalación y post-venta. Cada proyecto es 
              único y recibe nuestra atención personalizada para garantizar resultados excepcionales.
            </p>
          </div>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button 
              className="bg-gradient-to-r from-[#C6A76D] to-[#B0975F] text-white hover:from-[#B0975F] hover:to-[#9E8752] shadow-lg hover:shadow-xl px-8 py-4 text-lg font-bold transition-all duration-300"
              onClick={() => {
                const element = document.getElementById('contacto');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Conozca Más Sobre Nosotros
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutUs;